import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createStockNewsQuestion,
  deleteStockNewsQuestion,
  duplicateStockNewsQuestion,
  reopenAllStockNewsQuestions,
  removeStockNewsQuestionLabel,
  renameStockNewsQuestionLabel,
  updateStockNewsQuestion,
} from "../../shared/api/stockNewsQuestions";
import EntityLabelCell from "../entity/EntityLabelCell";
import EntityLabelFilterBar from "../entity/EntityLabelFilterBar";
import EntityLabelsModal from "../entity/EntityLabelsModal";
import EntityListBulkToolbar from "../entity/EntityListBulkToolbar";
import {
  getEntityListItemId,
  useEntityLabelFilter,
  useEntityListSelection,
} from "../../shared/hooks/useEntityLabelFilter";
import { getMessageForFetchError } from "../../shared/utils/apiError";
import { bulkApplyLabelsToItems, getSharedLabelsAmongItems } from "../../shared/utils/entityBulkLabels";
import { bulkSequentialDelete } from "../../shared/utils/bulkSequentialDelete";
import { formatBatchSummaryMessage } from "../../shared/utils/batchSummaryMessage";
import {
  entityLabelsEqual,
  ENTITY_LABEL_FILTER_UNLABELED_DISPLAY,
  getItemLabels,
  isUnlabeledEntityLabelFilter,
  normalizeEntityLabelsArray,
} from "../../shared/utils/entityLabelFilter";
import { openGoogleSearch } from "../../shared/utils/googleSearchUrl";
import {
  IconEdit,
  IconDelete,
  IconDuplicate,
  IconClose,
  IconReopen,
  iconBtnStyle,
  RowActionIcons,
} from "../../shared/icons/IconLib";
import { TableActionColumnHeader } from "../TableShell";
import StockDailyNewsActionItems from "./StockDailyNewsActionItems";
import StockDailyNewsInlineEditField from "./StockDailyNewsInlineEditField";

function StockDailyNewsQuestions({
  stockSession,
  serverUrl,
  questions = [],
  loading = false,
  onQuestionsChanged,
  onError,
  setConfirmDialog,
}) {
  const questionRows = Array.isArray(questions) ? questions : [];
  const {
    activeLabel,
    setActiveLabel,
    labelOptions,
    filteredItems: filteredQuestions,
    labelsForNewItem,
    addLabelName,
    renameLabelName,
    removeLabelName,
  } = useEntityLabelFilter({ items: questionRows });
  const {
    selectedIds,
    handleSelectAll,
    handleSelectOne,
    clearSelection,
  } = useEntityListSelection(getEntityListItemId);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkLabelsOpen, setBulkLabelsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBuffer, setEditBuffer] = useState("");

  useEffect(() => {
    clearSelection();
  }, [activeLabel, clearSelection]);

  const selectedQuestions = useMemo(
    () => questionRows.filter((question) => selectedIds.has(question.id)),
    [questionRows, selectedIds]
  );

  const sharedBulkLabels = useMemo(
    () => getSharedLabelsAmongItems(selectedQuestions),
    [selectedQuestions]
  );

  const handleAdd = useCallback(
    async (e) => {
      e?.preventDefault();
      const text = (draft || "").trim();
      if (!text) {
        setConfirmDialog({
          open: true,
          title: "Add question",
          message: "Enter question text before adding.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      setSaving(true);
      try {
        await createStockNewsQuestion(serverUrl, stockSession, text, {
          labels: labelsForNewItem,
        });
        setDraft("");
        await onQuestionsChanged();
      } catch (err) {
        onError(err.message || "Failed to add question");
      } finally {
        setSaving(false);
      }
    },
    [draft, labelsForNewItem, onError, onQuestionsChanged, serverUrl, setConfirmDialog, stockSession]
  );

  const handleAddLabel = useCallback(
    (name) => {
      const result = addLabelName(name);
      if (!result.ok) {
        setConfirmDialog({
          open: true,
          title: "Add label",
          message: result.error,
          mode: "alert",
          onConfirm: null,
        });
      }
      return result;
    },
    [addLabelName, setConfirmDialog]
  );

  const handleRenameLabel = useCallback(
    async (oldLabel, newLabel) => {
      setSaving(true);
      try {
        await renameStockNewsQuestionLabel(serverUrl, stockSession, { oldLabel, newLabel });
        const result = renameLabelName(oldLabel, newLabel);
        if (!result.ok) {
          throw new Error(result.error || "Failed to rename label");
        }
        await onQuestionsChanged();
      } catch (err) {
        onError(err.message || "Failed to rename label");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [onError, onQuestionsChanged, renameLabelName, serverUrl, stockSession]
  );

  const handleRemoveLabel = useCallback(
    async (label) => {
      setSaving(true);
      try {
        await removeStockNewsQuestionLabel(serverUrl, stockSession, label);
        const result = removeLabelName(label);
        if (!result.ok) {
          throw new Error(result.error || "Failed to remove label");
        }
        await onQuestionsChanged();
      } catch (err) {
        onError(err.message || "Failed to remove label");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [onError, onQuestionsChanged, removeLabelName, serverUrl, stockSession]
  );

  const handleApplyLabelsForQuestion = useCallback(
    async (question, labels) => {
      if (editingId === question.id || saving) return;
      const nextLabels = normalizeEntityLabelsArray(labels);
      if (entityLabelsEqual(getItemLabels(question), nextLabels)) {
        return;
      }
      setSaving(true);
      try {
        await updateStockNewsQuestion(serverUrl, stockSession, {
          questionId: question.id,
          labels: nextLabels,
        });
        await onQuestionsChanged();
      } catch (err) {
        onError(err.message || "Failed to update labels");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [editingId, onError, onQuestionsChanged, saving, serverUrl, stockSession]
  );

  const startEdit = useCallback((question) => {
    setEditingId(question.id);
    setEditBuffer(question.questionText || "");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditBuffer("");
  }, []);

  const commitEdit = useCallback(async () => {
    const text = (editBuffer || "").trim();
    if (!editingId) return;
    if (!text) {
      setConfirmDialog({
        open: true,
        title: "Edit question",
        message: "Question text cannot be empty.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setSaving(true);
    try {
      await updateStockNewsQuestion(serverUrl, stockSession, {
        questionId: editingId,
        questionText: text,
      });
      cancelEdit();
      await onQuestionsChanged();
    } catch (err) {
      onError(err.message || "Failed to update question");
    } finally {
      setSaving(false);
    }
  }, [
    cancelEdit,
    editBuffer,
    editingId,
    onError,
    onQuestionsChanged,
    serverUrl,
    setConfirmDialog,
    stockSession,
  ]);

  const closedCount = useMemo(
    () => questionRows.filter((q) => q.status === "done").length,
    [questionRows]
  );

  const handleToggleDone = useCallback(
    async (question) => {
      if (editingId === question.id || saving) return;
      setSaving(true);
      try {
        await updateStockNewsQuestion(serverUrl, stockSession, {
          questionId: question.id,
          status: question.status === "done" ? "open" : "done",
        });
        await onQuestionsChanged();
      } catch (err) {
        onError(err.message || "Failed to update question");
      } finally {
        setSaving(false);
      }
    },
    [editingId, onError, onQuestionsChanged, saving, serverUrl, stockSession]
  );

  const handleReopenAll = useCallback(() => {
    if (closedCount === 0) return;
    setConfirmDialog({
      open: true,
      title: "Reopen all",
      mode: "confirm",
      message: `Reopen ${closedCount} closed question${closedCount === 1 ? "" : "s"}?`,
      onConfirm: async () => {
        setSaving(true);
        try {
          await reopenAllStockNewsQuestions(serverUrl, stockSession);
          await onQuestionsChanged();
        } catch (err) {
          onError(err.message || "Failed to reopen questions");
        } finally {
          setSaving(false);
        }
      },
    });
  }, [closedCount, onError, onQuestionsChanged, serverUrl, setConfirmDialog, stockSession]);

  const handleDuplicate = useCallback(
    async (question) => {
      if (editingId === question.id || saving) return;
      setSaving(true);
      try {
        await duplicateStockNewsQuestion(serverUrl, stockSession, question.id);
        await onQuestionsChanged();
      } catch (err) {
        onError(err.message || "Failed to duplicate question");
      } finally {
        setSaving(false);
      }
    },
    [editingId, onError, onQuestionsChanged, saving, serverUrl, stockSession]
  );

  const handleDelete = useCallback(
    (question) => {
      setConfirmDialog({
        open: true,
        title: "Delete question",
        mode: "confirm",
        message: `Delete this question?\n\n"${question.questionText}"`,
        onConfirm: async () => {
          setSaving(true);
          try {
            await deleteStockNewsQuestion(serverUrl, stockSession, question.id);
            if (editingId === question.id) cancelEdit();
            await onQuestionsChanged();
          } catch (err) {
            onError(err.message || "Failed to delete question");
          } finally {
            setSaving(false);
          }
        },
      });
    },
    [
      cancelEdit,
      editingId,
      onError,
      onQuestionsChanged,
      serverUrl,
      setConfirmDialog,
      stockSession,
    ]
  );

  const handleQuestionClick = useCallback((question) => {
    if (editingId === question.id) return;
    openGoogleSearch(question.questionText);
  }, [editingId]);

  const openBulkLabels = useCallback(() => {
    if (selectedIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Update labels",
        message: "Please select at least one question to update.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setBulkLabelsOpen(true);
  }, [selectedIds.size, setConfirmDialog]);

  const handleBulkApplyLabels = useCallback(
    async (labels) => {
      setSaving(true);
      try {
        const { updated, skipped, errors } = await bulkApplyLabelsToItems({
          items: questionRows,
          selectedIds,
          labels,
          getId: getEntityListItemId,
          updateLabels: async (question, nextLabels) => {
            await updateStockNewsQuestion(serverUrl, stockSession, {
              questionId: question.id,
              labels: nextLabels,
            });
          },
        });
        await onQuestionsChanged();
        if (errors.length > 0) {
          setConfirmDialog({
            open: true,
            title: "Bulk label update",
            message: formatBatchSummaryMessage(
              `Updated ${updated} question(s) (${skipped} unchanged).`,
              updated + skipped,
              errors.length,
              errors
            ),
            mode: "alert",
            onConfirm: null,
          });
        }
        clearSelection();
        setBulkLabelsOpen(false);
      } catch (err) {
        onError(err.message || "Failed to update labels");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      clearSelection,
      onError,
      onQuestionsChanged,
      questionRows,
      selectedIds,
      serverUrl,
      setConfirmDialog,
      stockSession,
    ]
  );

  const deleteSelected = useCallback(() => {
    if (selectedIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Delete questions",
        message: "Please select at least one question to delete.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete questions",
      mode: "confirm",
      message: `Are you sure you want to delete ${selectedIds.size} question${selectedIds.size === 1 ? "" : "s"}?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (questionId) => {
            try {
              await deleteStockNewsQuestion(serverUrl, stockSession, questionId);
              return { ok: true, body: "" };
            } catch (err) {
              return { ok: false, body: err?.message || "Failed to delete question" };
            }
          },
          getMessageForFetchError
        );
        if (editingId && idsToDelete.includes(editingId)) cancelEdit();
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          mode: "alert",
          onConfirm: null,
        });
        clearSelection();
        await onQuestionsChanged();
        setDeleting(false);
      },
    });
  }, [
    cancelEdit,
    clearSelection,
    editingId,
    onQuestionsChanged,
    selectedIds,
    serverUrl,
    setConfirmDialog,
    stockSession,
  ]);

  return (
    <div className="panel stock-daily-news-panel">
      <div className="panel-title" style={{ marginBottom: 4 }}>
        Daily news analysis
      </div>
      <p className="panel-hint" style={{ marginTop: 0, marginBottom: 12 }}>
        Maintain a list of research questions. Click a question to search Google in a new tab. Mark
        questions done when complete; use Reopen all to reset closed questions.
      </p>

      <EntityLabelFilterBar
        items={questionRows}
        activeLabel={activeLabel}
        onActiveLabelChange={setActiveLabel}
        labelOptions={labelOptions}
        ariaLabel="Question labels"
        showUnlabeledFilter
      />

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          type="text"
          className="form-control"
          style={{ flex: "1 1 280px", minWidth: 0 }}
          placeholder="Add a news research question…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
          maxLength={500}
        />
        <button type="submit" className="btn btn-md btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Add question"}
        </button>
        <button
          type="button"
          className="btn btn-md btn-secondary"
          disabled={saving || !closedCount}
          title="Reopen all closed questions"
          onClick={handleReopenAll}
        >
          Reopen all
        </button>
      </form>

      <EntityListBulkToolbar
        selectedCount={selectedIds.size}
        onUpdateLabels={openBulkLabels}
        onDeleteSelected={deleteSelected}
        updating={saving && bulkLabelsOpen}
        deleting={deleting}
        ariaLabel="Bulk question actions"
      />

      {loading ? (
        <p className="panel-hint">Loading questions…</p>
      ) : questionRows.length === 0 ? (
        <p className="panel-hint">No questions yet. Add one above to start your daily news analysis.</p>
      ) : filteredQuestions.length === 0 ? (
        <p className="panel-hint">
          {isUnlabeledEntityLabelFilter(activeLabel)
            ? `No ${ENTITY_LABEL_FILTER_UNLABELED_DISPLAY.toLowerCase()} questions. Assign labels from the label icon in Action or choose All.`
            : `No questions with label “${activeLabel}”. Assign this label from the label icon in Action or choose All.`}
        </p>
      ) : (
        <div className="table-scroll">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th className="table-col-select">
                    <input
                      type="checkbox"
                      checked={
                        filteredQuestions.length > 0 &&
                        filteredQuestions.every((question) => selectedIds.has(question.id))
                      }
                      onChange={(e) => handleSelectAll(e, filteredQuestions)}
                      disabled={filteredQuestions.length === 0 || saving || deleting}
                      aria-label="Select all questions"
                    />
                  </th>
                  <th className="table-col-primary">Research topic</th>
                  <TableActionColumnHeader />
                </tr>
              </thead>
              <tbody>
                {filteredQuestions.map((question) => {
                  const isEditing = editingId === question.id;
                  const isDone = question.status === "done";
                  return (
                    <tr
                      key={question.id}
                      className={isDone ? "stock-daily-news-row--closed" : undefined}
                    >
                      <td className="table-col-select">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(question.id)}
                          onChange={(e) => handleSelectOne(question.id, e.target.checked)}
                          disabled={isEditing || saving || deleting}
                          aria-label={`Select question`}
                        />
                      </td>
                      <td className="table-col-primary">
                        {isEditing ? (
                          <StockDailyNewsInlineEditField
                            value={editBuffer}
                            onChange={(e) => setEditBuffer(e.target.value)}
                            disabled={saving}
                            maxLength={500}
                            onSave={commitEdit}
                            onCancel={cancelEdit}
                          />
                        ) : (
                          <button
                            type="button"
                            className="btn-link stock-daily-news-item-label"
                            title="Search Google"
                            onClick={() => handleQuestionClick(question)}
                          >
                            {question.questionText}
                          </button>
                        )}
                      </td>
                      <td className="text-center table-col-action">
                        {isEditing ? (
                          <span style={{ display: "inline-flex", gap: 8 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={saving}
                              onClick={() => commitEdit()}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              disabled={saving}
                              onClick={cancelEdit}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <RowActionIcons>
                            <EntityLabelCell
                              item={question}
                              iconOnly
                              labelOptions={labelOptions}
                              disabled={isEditing}
                              saving={saving}
                              ariaLabel="Question labels"
                              onApplyLabels={(labels) => handleApplyLabelsForQuestion(question, labels)}
                              onAddLabel={handleAddLabel}
                              onRenameLabel={handleRenameLabel}
                              onRemoveLabel={handleRemoveLabel}
                              setConfirmDialog={setConfirmDialog}
                            />
                            <button
                              type="button"
                              onClick={() => handleDuplicate(question)}
                              className="btn-link"
                              style={iconBtnStyle}
                              title="Duplicate"
                              disabled={saving}
                            >
                              <IconDuplicate size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => startEdit(question)}
                              className="btn-link"
                              style={iconBtnStyle}
                              title="Edit"
                              disabled={saving}
                            >
                              <IconEdit size={18} />
                            </button>
                            {isDone ? (
                              <button
                                type="button"
                                onClick={() => handleToggleDone(question)}
                                className="btn-link"
                                style={iconBtnStyle}
                                title="Reopen"
                                disabled={saving}
                              >
                                <IconReopen size={18} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleToggleDone(question)}
                                className="btn-link-muted"
                                style={iconBtnStyle}
                                title="Mark done"
                                disabled={saving}
                              >
                                <IconClose size={18} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDelete(question)}
                              className="btn-link-danger"
                              style={iconBtnStyle}
                              title="Delete"
                              disabled={saving}
                            >
                              <IconDelete size={18} />
                            </button>
                          </RowActionIcons>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <EntityLabelsModal
        open={bulkLabelsOpen}
        onClose={() => setBulkLabelsOpen(false)}
        labelOptions={labelOptions}
        selectedLabels={sharedBulkLabels}
        onApplyLabels={handleBulkApplyLabels}
        onAddLabel={handleAddLabel}
        onRenameLabel={handleRenameLabel}
        onRemoveLabel={handleRemoveLabel}
        setConfirmDialog={setConfirmDialog}
        saving={saving}
        subtitle={`Select labels to apply to ${selectedIds.size} selected question${selectedIds.size === 1 ? "" : "s"}. Rename or delete from the same row.`}
      />
    </div>
  );
}

function StockDailyNewsAnalysis({
  stockSession,
  serverUrl,
  questions,
  questionsLoading,
  onQuestionsChanged,
  actionItems,
  actionItemsLoading,
  onActionItemsChanged,
  onError,
  setConfirmDialog,
}) {
  const safeQuestions = Array.isArray(questions) ? questions : [];
  const safeActionItems = Array.isArray(actionItems) ? actionItems : [];

  return (
    <div className="stock-daily-news-layout">
      <StockDailyNewsQuestions
        stockSession={stockSession}
        serverUrl={serverUrl}
        questions={safeQuestions}
        loading={questionsLoading}
        onQuestionsChanged={onQuestionsChanged}
        onError={onError}
        setConfirmDialog={setConfirmDialog}
      />
      <StockDailyNewsActionItems
        stockSession={stockSession}
        serverUrl={serverUrl}
        actionItems={safeActionItems}
        loading={actionItemsLoading}
        onActionItemsChanged={onActionItemsChanged}
        onError={onError}
        setConfirmDialog={setConfirmDialog}
      />
    </div>
  );
}

export default StockDailyNewsAnalysis;
